import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash';
import { STAGE_OPTIONS_MAP } from './deal-stage-selector';

type DealStatusSelectorCombobox = {
  id: Deal['id'];
  shortId: Deal['short_id'];
  afterOnChange?: () => void;
  onSuccess?: (data: Deal, variables: { stage: Deal['stage'] }) => void;
  onError?: (error: Error, variables: { stage: Deal['stage'] }) => void;
};

export function DealStageSelectorCombobox({
  id,
  shortId,
  afterOnChange,
  onSuccess,
  onError,
}: DealStatusSelectorCombobox) {
  const queryClient = useQueryClient();

  const updateStageMutation = useMutation({
    onMutate: async ({ stage }) => {
      dealStore.updateStage({ id, stage });
    },
    mutationFn: async ({ stage }: { stage: Deal['stage'] }) => {
      const response = await pdkAxios
        .post(`/deals/${id}/stage`, { stage })
        .catch((e) => {
          console.error('Error updating stage', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating stage', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', shortId] });
    },
  });

  const handleStageChange = (newStage: Deal['stage']) => {
    updateStageMutation.mutate({
      stage: newStage,
    });

    afterOnChange?.();
  };

  return (
    <Combobox
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_CONTRACT_STATUS}</KeyboardKeyIcon>
      }
      placeholder="Set contract status..."
      // onChange={handleStatusChange}
      emptyMessage="No contract status found"
      options={STAGE_OPTIONS_MAP}
      // side={side}
      // align="start"
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = STAGE_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handleStageChange(option.value);
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onChange={handleStageChange}
      setOpen={noop}
    ></Combobox>
  );
}
